import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Phone, ShieldCheck, BadgeCheck, Award, Thermometer, Handshake, ClipboardCheck } from 'lucide-react';
import { COMPANY, TRUST_ITEMS } from '../lib/siteConfig';
import { trackCall } from '../lib/analytics';
import { GoogleRatingLine, useReviewSummary } from '../lib/siteData';
import { Breadcrumbs } from './Breadcrumbs';
import { srcSetFor, portraitSrcSet, dims, SIZES } from '../lib/img';
import { useIsHydrating } from '../lib/hydration';
import { api } from '../lib/api';
import { REFERENCE_RANGES_PATH, costAnswer } from '../lib/referenceRanges';

// Visually obvious placeholder token, per the brief — nothing ships blank unnoticed.
export const Token = ({ value }) => (
  <span className="idb-token" title="Placeholder — replace with real value before launch">{value}</span>
);

// Scroll-in reveal (opacity + 18px rise, 550ms, ease-out-expo) driven by IntersectionObserver
// and a CSS transition (.idb-reveal in index.css). Deliberately NOT framer-motion: this component
// sits on every page, and keeping the ~35 KB (gzip) motion runtime out of the shared bundle is a
// measurable LCP win for the paid-search landing page. The calculator keeps its own motion code
// in its lazy chunk. Reduced-motion users and environments without IntersectionObserver see the
// content immediately; a 2.5s fail-safe guarantees nothing can stay hidden.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

//
// Pre-rendered pages (Phase 59): the HTML ships with every reveal already VISIBLE — nothing above
// the fold may start hidden, and the hydration render must match that markup. After hydration,
// anything already inside the viewport simply stays visible (never hide painted content); blocks
// below the fold go back to the hidden state before the browser paints and animate in on scroll
// exactly as before.
export const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const hydrating = useIsHydrating();
  const hydratedFromMarkup = useRef(hydrating);
  const [visible, setVisible] = useState(() => prefersReducedMotion() || typeof IntersectionObserver === 'undefined');
  useLayoutEffect(() => {
    if (!hydratedFromMarkup.current || visible) return;
    const el = ref.current;
    if (el && el.getBoundingClientRect().top < window.innerHeight) setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el) { setVisible(true); return undefined; }
    const failSafe = setTimeout(() => setVisible(true), 2500);
    // Low threshold + bottom margin so tall sections on short/mobile viewports still trigger
    // (a high threshold can never be reached by a section taller than the viewport).
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    return () => { clearTimeout(failSafe); io.disconnect(); };
  }, [visible]);
  const shown = visible || hydrating;
  return (
    <div ref={ref} className={`idb-reveal${shown ? ' is-visible' : ''} ${className}`} style={delay ? { transitionDelay: `${delay}s` } : undefined}>
      {children}
    </div>
  );
};

// Mobile polish §6 (page length): 40px section padding under 640px; sm and up unchanged.
export const Section = ({ children, className = '', ...rest }) => (
  <section className={`relative py-10 sm:py-20 ${className}`} {...rest}>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

/*
 * Full-bleed hero image with a phone-portrait source (mobile polish §4). Under 768px the browser
 * takes the portrait-cropped set (750w / 1080w, subject centred) instead of stretching an 800w
 * landscape file ~3x into a 375x784 cover; wider screens keep the landscape set exactly as before.
 * Carries explicit width/height (lib/img dims) so the box is sized before the bytes arrive.
 */
export const HeroPicture = ({ src, originalWidth, alt = '', className = '', loading = 'eager', fetchPriority, ...rest }) => {
  const portrait = portraitSrcSet(src);
  const size = dims(src);
  return (
    <picture>
      {portrait && <source media="(max-width: 767px)" srcSet={portrait} sizes="100vw" />}
      <img
        src={src}
        srcSet={srcSetFor(src, originalWidth)}
        sizes={SIZES.full}
        alt={alt}
        aria-hidden={alt ? undefined : 'true'}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        {...size}
        {...rest}
      />
    </picture>
  );
};

// Oversized decorative section numeral (engineered watermark)
export const Watermark = ({ num, dark = false }) => (
  <span aria-hidden="true" data-num={num} className={`idb-watermark ${dark ? 'idb-watermark-dark' : ''} hidden sm:block`} />
);

// Section heading. The small mono "eyebrow" label (and "01 /" counter) that used to sit above the
// title was removed site-wide on the owner's request — the heading now leads.
export const SectionTitle = ({ title, sub, dark = false, center = false }) => (
  <Reveal className={center ? 'text-center mx-auto max-w-2xl' : 'max-w-2xl'}>
    <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight ${dark ? 'text-white' : 'text-[#121417]'}`}>{title}</h2>
    <div className="mt-4 max-md:mt-3 flex items-center gap-3">
      <div className={`idb-gradient-rule w-16 ${center ? 'mx-auto' : ''}`} />
      {!center && <div className={`idb-dimension-rule flex-1 max-w-[120px] ${dark ? 'idb-dimension-rule-dark' : ''}`} />}
    </div>
    {sub && <p className={`mt-4 max-md:mt-3 text-sm sm:text-base leading-relaxed ${dark ? 'text-[#8FA0AE]' : 'text-[#454B52]'}`}>{sub}</p>}
  </Reveal>
);

// Interior page hero — premium "spec cover sheet": blueprint grid + grain + mono kicker + signature rule
// Page hero. No eyebrow label above the H1 (removed site-wide on the owner's request).
/*
 * Page hero. `heading` (SEO Batch A, Part 5) is the page's <h1> — the service + place, set in the
 * same small mono eyebrow style the homepage hero uses — while `title`, the slogan, keeps its
 * display styling exactly as before but as a <p>. Without `heading` the slogan is the <h1> (legacy;
 * /projects keeps this on purpose).
 */
export const PageHeader = ({ heading, title, sub, image, crumbs }) => (
  <div className="relative bg-[#121417] idb-grain idb-blueprint idb-blueprint-dark overflow-hidden">
    {image && (
      <HeroPicture src={image} className="absolute inset-0 h-full w-full object-cover opacity-25" loading="eager" />
    )}
    <div className="absolute inset-0 bg-gradient-to-b from-[#121417]/70 via-[#121417]/60 to-[#121417]" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      {/* Visible breadcrumbs (SEO Batch B, Part 1.5) sit above the heading, matching the page's BreadcrumbList JSON-LD. */}
      {crumbs && <Breadcrumbs crumbs={crumbs} tone="dark" className="mb-5" />}
      {heading ? (
        <>
          <h1 className="font-mono-idb text-xs sm:text-sm uppercase tracking-[0.18em] text-[rgba(244,241,234,0.72)]" data-testid="pageheader-h1">{heading}</h1>
          <p className="mt-3 max-w-3xl text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white" data-testid="pageheader-title">{title}</p>
        </>
      ) : (
        <h1 className="max-w-3xl text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white" data-testid="pageheader-title">{title}</h1>
      )}
      <div className="mt-5 flex items-center gap-3">
        <div className="idb-gradient-rule w-20" />
        <div className="idb-dimension-rule idb-dimension-rule-dark flex-1 max-w-[160px]" />
      </div>
      {sub && <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-[#C9D2DA]">{sub}</p>}
    </div>
    <div className="absolute bottom-0 inset-x-0 idb-gradient-hairline z-10" />
  </div>
);

const TRUST_ICONS = { BadgeCheck, ShieldCheck, Award, Thermometer, Handshake, ClipboardCheck };

/**
 * Trust strip — items come from TRUST_ITEMS in lib/siteConfig.js (brief 01 §2), rendered in order.
 * ≥768px: one centred row. Phones: two rows that swipe horizontally together (grid-flow-col over two
 * grid rows), so six chips never stack six deep; no page-level horizontal scroll (container clips).
 * `withRating` (homepage only — SEO Batch B, Part 3.5) appends the "{rating}★ on Google ({count}
 * reviews)" pill; it renders nothing until the owner has entered a Google count of 1 or more.
 */
export const TrustBar = ({ className = '', withRating = false }) => (
  <div className={`bg-[#FFFCF6]/90 backdrop-blur border-y border-[#D7D2C8] ${className}`} data-testid="trust-bar">
    {/* ≥lg: full-width, slightly tighter tracking/padding so all six pills share one row from ~1366px up
        (six 11px uppercase mono pills need ~1,300px; a 1280px desktop wraps gracefully to a centred second row). */}
    <ul className="max-w-7xl lg:max-w-none mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 lg:gap-1.5 max-md:grid max-md:grid-rows-2 max-md:grid-flow-col max-md:auto-cols-max max-md:justify-start max-md:overflow-x-auto idb-scroll-x list-none m-0" aria-label="Licensing and credentials">
      {TRUST_ITEMS.map((item) => {
        const Icon = TRUST_ICONS[item.icon] || BadgeCheck;
        return (
          <li key={item.key} className={`idb-spec inline-flex items-center gap-1.5 whitespace-nowrap lg:tracking-[0.03em] lg:px-2 ${item.emphasis ? 'text-[#121417]' : 'text-[#454B52]'}`} data-testid={`trust-bar-${item.key}`}>
            <Icon className="h-3.5 w-3.5 text-[#3E6F8E]" aria-hidden="true" /> {item.label}
          </li>
        );
      })}
      {withRating && <TrustBarRating />}
    </ul>
  </div>
);

/* The Google rating pill for the homepage trust bar — nothing at all (no empty slot) until a count of 1+ exists. */
const TrustBarRating = () => {
  const { stats } = useReviewSummary();
  if (!stats?.showRatingLine) return null;
  return (
    <li className="inline-flex" data-testid="trust-bar-google-rating">
      <GoogleRatingLine className="idb-spec text-[#121417] lg:tracking-[0.03em] lg:px-2 hover:text-[#3E6F8E]" testId="trust-bar-google-rating-line" />
    </li>
  );
};

export const CtaBand = ({ title = 'Ready for a system that is actually sized for your home?', sub = 'Answer eleven quick questions and get an honest planning range — then we confirm it with a free on-site measure.', primaryLabel = 'Get My Estimate Range', primaryTo = '/estimate-calculator' }) => (
  <Section className="bg-[#121417] idb-grain idb-blueprint idb-blueprint-dark overflow-hidden" data-testid="cta-band">
    <div className="absolute top-0 inset-x-0 idb-gradient-hairline z-10" />
    <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-8 max-md:gap-6 justify-between">
      <div className="max-w-2xl">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">{title}</h2>
        <p className="mt-3 text-[#8FA0AE] leading-relaxed">{sub}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 shrink-0">
        <Link to={primaryTo} data-testid="cta-band-primary" className="h-12 px-6 inline-flex items-center justify-center rounded-xl idb-btn-copper text-sm active:scale-[0.98]">
          {primaryLabel}
        </Link>
        <a href={COMPANY.phoneMainHref} onClick={() => trackCall('cta_band')} data-testid="cta-band-call" className="h-12 px-6 inline-flex items-center justify-center gap-2 rounded-xl border border-[rgba(244,241,234,0.22)] text-white text-sm font-medium hover:border-[rgba(244,241,234,0.38)] hover:bg-[rgba(255,252,246,0.06)] transition-colors duration-200">
          <Phone className="h-4 w-4 text-[#7FA7B8]" /> Call {COMPANY.phoneMain}
        </a>
      </div>
    </div>
  </Section>
);

// Cities-served marquee ticker — subtle, pauses on hover/focus, grid-wraps under reduced motion
export const CityTicker = ({ cities = [], label = 'Cities we serve' }) => (
  <div className="rounded-2xl border border-[rgba(244,241,234,0.14)] bg-[#121417] idb-grain overflow-hidden idb-brackets-dark relative p-4 sm:p-5" data-testid="cities-ticker">
    <p className="relative z-10 font-mono-idb text-[11px] max-md:text-xs uppercase tracking-[0.18em] text-[rgba(244,241,234,0.55)] mb-3">{label}</p>
    <div className="idb-marquee relative z-10">
      <div className="idb-marquee-track">
        {[...cities, ...cities].map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="idb-spec idb-spec-dark whitespace-nowrap"
            data-testid={i < cities.length ? `service-area-city-chip-${c.toLowerCase().replace(/\s+/g, '-')}` : undefined}
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  </div>
);

/*
 * The FAQ cost answer: renders the build-time engine numbers first (identical to the pre-rendered HTML,
 * so hydration matches), then re-fetches /api/pricing/reference-ranges after mount and swaps in the live
 * numbers — the same pattern as lib/siteData.js. On any error the baked sentence simply stays.
 */
export const LiveCostAnswer = ({ fallback, testId }) => {
  const [text, setText] = useState(fallback);
  useEffect(() => {
    let alive = true;
    api.get(REFERENCE_RANGES_PATH)
      .then(({ data }) => { if (alive && Array.isArray(data?.systems) && data.systems.length) setText(costAnswer(data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return <span data-testid={testId} data-live={text === fallback ? 'baked' : 'live'}>{text}</span>;
};

/*
 * FAQ accordion (Prompt 13): every answer is in the HTML — open or closed — so the pre-rendered snapshot
 * (and curl, and a crawler) carries the full text, including the engine-priced cost answer. The Radix
 * accordion only mounted an answer while it was open, which is why the live /faq snapshot had headings
 * and empty panels. Same look and motion (chevron, 200 ms height ease via grid-rows), same data-state
 * attributes; a closed panel is `inert` (unfocusable, hidden from assistive tech) and aria-hidden.
 * One open at a time, collapsible, keyboard-operable via the native <button>. Server and first client
 * render are identical (all closed), so hydration never mismatches.
 */
export const FaqBlock = ({ items, testId = 'faq-accordion' }) => {
  const [open, setOpen] = useState(null);
  const idBase = useId();
  return (
    <div className="space-y-3" data-testid={testId} data-faq-block="true">
      {items.map((f, i) => {
        const isOpen = open === i;
        const panelId = `${idBase}-panel-${i}`;
        const triggerId = `${idBase}-trigger-${i}`;
        return (
          <div key={f.q} data-state={isOpen ? 'open' : 'closed'} className="rounded-[14px] border border-[#D7D2C8] bg-[#FFFCF6] px-5 shadow-[var(--idb-shadow-sm)]" data-testid={`${testId}-item-${i}`}>
            <h3 className="flex">
              <button
                type="button"
                id={triggerId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                data-state={isOpen ? 'open' : 'closed'}
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex flex-1 items-center justify-between gap-4 py-4 text-left font-display font-semibold text-[15px] text-[#121417] hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3E6F8E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCF6] rounded-[8px]"
                data-testid={`${testId}-trigger-${i}`}
              >
                {f.q}
                <ChevronDown className={`h-4 w-4 shrink-0 text-[#61676E] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              aria-hidden={!isOpen}
              inert={isOpen ? undefined : true}
              data-state={isOpen ? 'open' : 'closed'}
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              data-testid={`${testId}-panel-${i}`}
            >
              <div className="overflow-hidden min-h-0">
                <div className="text-sm leading-relaxed text-[#454B52] pb-5">
                  {f.live === 'cost' ? <LiveCostAnswer fallback={f.a} testId={`${testId}-live-cost`} /> : f.a}
                  {/* Optional follow-on link ([label, path]) — e.g. the financing answer points at /financing. */}
                  {Array.isArray(f.link) && (
                    <p className="mt-2">
                      <Link to={f.link[1]} className="inline-flex items-center gap-1 text-sm font-medium text-[#2F5E78] underline-offset-2 hover:underline max-md:min-h-11" data-testid={`${testId}-link-${i}`}>
                        {f.link[0]} →
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/*
 * FIX-02 — one real before/after photo pair (never an AI rendering or stock photo). Same visual
 * pattern used on the homepage "Proof, side by side" teaser and on /projects, now a single shared
 * component so both stay in sync. `before`/`after` are Project photo objects: {url, width,
 * height, alt}.
 */
export const BeforeAfterPair = ({ before, after, size = 'half', frame = true, imgClassName = 'h-52 sm:h-72 w-full object-cover', className = '' }) => (
  <div className={`${frame ? 'idb-card idb-card-hover idb-brackets overflow-hidden' : 'overflow-hidden rounded-[14px]'} ${className}`} data-testid="before-after-pair">
    <div className="grid grid-cols-2 gap-px bg-[#D7D2C8]">
      <div className="relative">
        <img src={before.url} srcSet={srcSetFor(before.url, before.width)} sizes={SIZES[size] || SIZES.half} width={before.width} height={before.height} alt={before.alt || 'Before photo'} loading="lazy" className={imgClassName} data-testid="before-after-before-img" />
        <span className="absolute top-3 left-3 idb-spec idb-spec-dark bg-[#121417]/80 backdrop-blur-sm" data-testid="before-after-before-label">Before</span>
      </div>
      <div className="relative">
        <img src={after.url} srcSet={srcSetFor(after.url, after.width)} sizes={SIZES[size] || SIZES.half} width={after.width} height={after.height} alt={after.alt || 'After photo'} loading="lazy" className={imgClassName} data-testid="before-after-after-img" />
        <span className="absolute top-3 left-3 idb-spec bg-[#FFFCF6]/90 backdrop-blur-sm text-[#2F6B57] border-[#2F6B57]/40" data-testid="before-after-after-label">After</span>
      </div>
    </div>
  </div>
);


